import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  GraduationCap,
  BookOpen,
  Shield,
  ChevronDown,
  Check,
  Sparkles,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { useAuth, Role } from '@shared/auth/AuthContext';

export interface ViewOption {
  id: Role;
  name: string;
  badge: string;
  desc: string;
  icon: React.ElementType;
  homePath: string;
  colorClass: string;
  badgeClass: string;
}

export const VIEW_OPTIONS: ViewOption[] = [
  {
    id: 'student',
    name: 'Student View',
    badge: 'Candidate',
    desc: 'Learner dashboard, CBT tests, speed quizzes, course player & rewards',
    icon: GraduationCap,
    homePath: '/sis/dashboard',
    colorClass: 'text-sky-400',
    badgeClass: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  },
  {
    id: 'teacher',
    name: 'Teacher View',
    badge: 'Faculty',
    desc: 'Faculty command center, live classroom host, question authoring & doubts',
    icon: BookOpen,
    homePath: '/hr/dashboard',
    colorClass: 'text-amber-400',
    badgeClass: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  },
  {
    id: 'owner',
    name: 'Admin / Owner View',
    badge: 'Full Suite',
    desc: 'Full 10-suite institutional control, BI analytics, finance, CRM & ops',
    icon: Shield,
    homePath: '/bi/executive',
    colorClass: 'text-primary',
    badgeClass: 'bg-primary/10 text-primary border-primary/20',
  },
];

export const RoleViewSwitcher: React.FC = () => {
  const { role, setRole, hasSuite } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Map any other role to one of the 3 primary views
  const currentView = VIEW_OPTIONS.find((v) => {
    if (role === 'student' || role === 'parent') return v.id === 'student';
    if (role === 'teacher') return v.id === 'teacher';
    return v.id === 'owner';
  }) || VIEW_OPTIONS[2];

  const handleSelectView = (view: ViewOption) => {
    if (view.id === role) return;

    setRole(view.id);

    const studentHome = hasSuite('sis') ? '/sis/dashboard' : '/core/dashboard';
    const teacherHome = hasSuite('hr') ? '/hr/dashboard' : '/core/dashboard';
    const ownerHome = hasSuite('bi') ? '/bi/executive' : '/core/dashboard';

    // If current path isn't relevant to the new view, redirect to the view's primary home
    if (view.id === 'student') {
      const allowedPrefixes = ['/core', '/lms', '/sis', '/fees/invoices', '/communication/chat', '/communication/forum'];
      const isAllowed = allowedPrefixes.some((p) => location.pathname.startsWith(p));
      if (!isAllowed || location.pathname.includes('/hr') || location.pathname.includes('/bi')) {
        navigate(studentHome);
      }
    } else if (view.id === 'teacher') {
      const allowedPrefixes = ['/core', '/hr', '/lms', '/sis/students', '/scheduling', '/communication'];
      const isAllowed = allowedPrefixes.some((p) => location.pathname.startsWith(p));
      if (!isAllowed || location.pathname.includes('/platform') || location.pathname.includes('/fees/ledger')) {
        navigate(teacherHome);
      }
    } else {
      // Owner/Admin
      if (location.pathname === '/sis/dashboard') {
        navigate(ownerHome);
      }
    }
  };

  const IconComp = currentView.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-8 px-2.5 rounded-lg bg-navbar-accent/60 hover:bg-navbar-accent border border-navbar-border text-navbar-foreground text-xs font-medium flex items-center gap-2 transition-all cursor-pointer shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          title="Switch Active Role View (Student, Teacher, Admin/Owner)"
        >
          <div className="flex items-center gap-1.5">
            <IconComp className={`h-3.5 w-3.5 ${currentView.colorClass}`} />
            <span className="font-semibold tracking-tight hidden sm:inline-block">
              {currentView.name}
            </span>
          </div>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border hidden md:inline-block ${currentView.badgeClass}`}>
            {currentView.badge}
          </span>
          <ChevronDown className="h-3 w-3 text-navbar-muted ml-0.5" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 p-1.5" sideOffset={8}>
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground p-0">
            Switch Platform View
          </DropdownMenuLabel>
          <span className="text-[10px] text-primary font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Live Context
          </span>
        </div>
        <DropdownMenuSeparator className="my-1" />

        <div className="space-y-1">
          {VIEW_OPTIONS.map((view) => {
            const isSelected = currentView.id === view.id;
            const ViewIcon = view.icon;

            return (
              <DropdownMenuItem
                key={view.id}
                onClick={() => handleSelectView(view)}
                className={`p-2.5 rounded-lg flex items-start justify-between cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-accent/80 font-medium ring-1 ring-primary/40'
                    : 'hover:bg-muted focus:bg-muted'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`p-1.5 rounded-md mt-0.5 bg-muted/60 ${view.colorClass}`}>
                    <ViewIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-foreground">{view.name}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.2 rounded border ${view.badgeClass}`}>
                        {view.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                      {view.desc}
                    </p>
                  </div>
                </div>

                {isSelected && (
                  <Check className="h-4 w-4 text-primary shrink-0 ml-2 mt-1" />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleViewSwitcher;
